/**
 * JPDZ Backend AI Service
 * Handles server-side AI inference via n8n workflow with OpenRouter
 */

import { useAuthStore } from '@/store/auth';

export type AIMode = 'gemini' | 'backend' | 'detecting' | 'unavailable';

export interface AIProvider {
  name: string;
  isAvailable(): Promise<boolean>;
  generate(
    prompt: string,
    chatHistory?: Array<{ role: 'user' | 'assistant'; content: string }>
  ): AsyncGenerator<any>;
  initialize?(): Promise<void>;
}

class BackendProvider implements AIProvider {
  name = 'JPDZ Backend (n8n + OpenRouter)';
  private baseURL: string;

  constructor() {
    this.baseURL = process.env.NODE_ENV === 'production'
      ? '' // Production API URL
      : 'http://localhost:5000'; // Development API URL
  }

  async isAvailable(): Promise<boolean> {
    try {
      // Get authentication token for health check
      const token = useAuthStore.getState().token || localStorage.getItem('jpdz_token');
      const headers: Record<string, string> = {};

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(`${this.baseURL}/api/health`, {
        headers,
        credentials: 'include'
      });
      return response.ok;
    } catch {
      return false;
    }
  }

  async* generate(
    prompt: string,
    chatHistory?: Array<{ role: 'user' | 'assistant'; content: string }>,
    chatId?: string
  ): AsyncGenerator<any> {

    try {
      // Get authentication token
      const token = useAuthStore.getState().token || localStorage.getItem('jpdz_token');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(`${this.baseURL}/api/chat/stream`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          query: prompt,
          chat_id: chatId, // Use actual chat ID or undefined for new chat
        }),
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication required. Please log in again.');
        }
        throw new Error(`Backend API error: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              yield data;
            } catch (e) {
            }
          }
        }
      }
    } catch (error) {
      throw new Error('Failed to generate response from backend');
    }
  }
}

/**
 * Backend AI Service - Single Provider (n8n workflow with OpenRouter)
 * No WebLLM, no Ollama - Backend only for all users
 */
export class LocalAIService {
  private backendProvider: BackendProvider;
  private mode: AIMode = 'backend';

  constructor() {
    this.backendProvider = new BackendProvider();
  }

  async initialize(): Promise<AIMode> {
    this.mode = 'backend';

    try {
      const isAvailable = await this.backendProvider.isAvailable();
      if (isAvailable) {
        return this.mode;
      } else {
        this.mode = 'unavailable';
        return this.mode;
      }
    } catch (error) {
      this.mode = 'unavailable';
      return this.mode;
    }
  }

  async* generate(
    prompt: string,
    chatHistory?: Array<{ role: 'user' | 'assistant'; content: string }>,
    chatId?: string
  ): AsyncGenerator<any> {
    if (this.mode !== 'backend') {
      throw new Error('Backend AI not initialized. Please run initialization first.');
    }


    let chunkCount = 0;
    for await (const chunk of this.backendProvider.generate(prompt, chatHistory, chatId)) {
      chunkCount++;
      yield chunk;
    }

  }

  getCurrentMode(): AIMode {
    return this.mode;
  }

  getCurrentProvider(): AIProvider {
    return this.backendProvider;
  }

  // No WebLLM initialization needed - backend is always ready
  async initializeWebLLM(): Promise<void> {
    // No-op - backend doesn't need initialization like WebLLM
  }

  // Switch provider - no-op since we only have backend
  async switchProvider(_providerName: string): Promise<boolean> {
    // Only backend is supported
    return true;
  }
}

// Global instance
export const localAI = new LocalAIService();
